<template>
  <div class="check-groups">
    <h1 class="text-2xl font-bold text-center mt-6">Resumen de Grupos</h1>
    <div class="mt-3 mb-6 export-actions">
      <button class="button-excel" @click="exportToExcel">Descargar usuarios registrados</button>
      <label v-if="$canView('superadministrador') && eventContext.has_family" class="scope-selector">
        <span>Datos a mostrar</span>
        <select v-model="eventScope" @change="fetchGroups">
          <option v-for="option in eventScopeOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>
      <label v-if="$canView('superadministrador')" class="wordpress-toggle">
        <input type="checkbox" v-model="includeWordpress" @change="fetchGroups" />
        Incluir grupo WordPress en métricas visibles
      </label>
    </div>
    <!-- Resumen General -->
    <div class="general-summary mb-8">
      <p>Cantidad de grupos: {{ totalGroups }}</p>
      <p>Cantidad de usuarios completos: {{ totalCompleteUsers }}</p>
      <p>Cantidad de usuarios incompletos: {{ totalIncompleteUsers }}</p>
      <p>Cantidad de grupos con todos los usuarios completos: {{ groupsComplete }}</p>
      <p>Cantidad de grupos con al menos un usuario completo: {{ groupsWithAtLeastOneComplete }}</p>
      <p>Cantidad de grupos con usuarios incompletos: {{ groupsIncomplete }}</p>
    </div>

    <!-- Gráficas -->
     <div class="contenedor-graficas mb-20">
      <PieChart :chart-data="completeGroupsChartData" chart-title="Grupos Completos vs Restantes"/>
      <PieChart :chart-data="groupsAtLeastOneCompleteChartData" chart-title="Grupos con al menos un usuario completo"/>
      <PieChart :chart-data="userCompletenessChartData" chart-title="Usuarios Completos vs Incompletos"/>
      <PieChart :chart-data="qrChartData" chart-title="Usuarios con datos para generar QR"/>
    </div>

    <!-- Tabla de Grupos -->
    <table class="min-w-full table-auto text-left">
      <thead>
        <tr>
          <th v-if="showEventColumn">Evento</th>
          <th>Nombre del grupo</th>
          <th>{{ labelStaff }}</th>
          <th>{{ labelAsistentes }}</th>
          <th>{{ labelExpositor }}</th>
          <th>Total Registrados</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(group, id) in groups" :key="id">
          <td v-if="showEventColumn">{{ group.evento?.nombre || '' }}</td>
          <td>{{ group.datadelgrupo.name }}</td>
          <td>{{ categorySummaryText(group, 'Staff', 'cantidad_staff') }}</td>
          <td>{{ categorySummaryText(group, 'Asistente', 'cantidad_asistente') }}</td>
          <td>{{ categorySummaryText(group, 'Conferencista', 'cantidad_conferencista') }}</td>
          <td>{{ totalRegisteredUsersGroup(group.staffs.usuarios) }} / {{ totalAllowedUsersGroup(group) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import axios from "axios";
import ExcelJS from "exceljs";
import PieChart from "@/components/PieChart";

const MAX_MULTI_OPTION_COLS = 12;

const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (m) => m.toUpperCase());

export default {
  name: "CheckGroups",
  components: { PieChart },
  props: {
    eventoToken: { type: String, required: true },
  },
  data() {
    return {
      groups: {},
      // requerido/mandatorio (para completar)
      camposObligatorios: {},
      // metadata completa (para export dinámico)
      camposMeta: [],
      includeWordpress: false,
      groupsInitialized: false,
      labelStaff: "Staff",
      labelAsistentes: "Asistentes",
      labelExpositor: "Expositores",
      eventScope: "current",
      eventContext: {},
      eventSchemas: {},
      selectedEventIds: [],
    };
  },
  computed: {
    permissionsReady() {
      return this.$role?.roleLoaded === true;
    },
    canSeeWordpress() {
      return this.$canView('superadministrador');
    },
    showEventColumn() {
      return (this.selectedEventIds || []).length > 1 || this.eventScope !== "current";
    },
    eventScopeOptions() {
      const current = this.eventContext?.current || {};
      const parent = this.eventContext?.parent || {};
      const isChild = !!this.eventContext?.is_child;
      const children = this.eventContext?.children || [];

      if (isChild) {
        const options = [
          { value: "current", label: `Solo este subevento: ${current.nombre || "actual"}` },
          { value: "parent", label: `Solo evento principal: ${parent.nombre || "principal"}` },
        ];
        children
          .filter((child) => Number(child.id_evento) !== Number(current.id_evento))
          .forEach((child) => options.push({ value: `event:${child.id_evento}`, label: `Solo subevento: ${child.nombre}` }));
        options.push({ value: "family", label: "Evento principal + todos los subeventos" });
        return options;
      }

      const options = [
        { value: "current", label: `Solo evento principal: ${current.nombre || "actual"}` },
      ];
      children.forEach((child) => options.push({ value: `event:${child.id_evento}`, label: `Solo subevento: ${child.nombre}` }));
      options.push({ value: "family", label: "Evento principal + todos los subeventos" });
      return options;
    },
    totalGroups() {
      return Object.keys(this.groups).length;
    },
    totalCompleteUsers() {
      return this.computeTotalUsers("complete");
    },
    totalIncompleteUsers() {
      return this.computeTotalUsers("incomplete");
    },
    groupsComplete() {
      return this.computeGroupCounts("complete");
    },
    groupsWithAtLeastOneComplete() {
      return this.computeGroupCounts("atLeastOneComplete");
    },
    groupsIncomplete() {
      return this.computeGroupCounts("incomplete");
    },
    completeGroupsChartData() {
      return {
        labels: [
          `Grupos Completos: ${this.groupsComplete}`,
          `Grupos Restantes: ${this.totalGroups - this.groupsComplete}`,
        ],
        datasets: [
          {
            data: [this.groupsComplete, this.totalGroups - this.groupsComplete],
            backgroundColor: ["#59B4F1", "#d5d5d5"],
            hoverBackgroundColor: ["#59B4F1", "#d5d5d5"],
          },
        ],
      };
    },
    groupsAtLeastOneCompleteChartData() {
      return {
        labels: [
          `Grupos con al menos un usuario completo: ${this.groupsWithAtLeastOneComplete}`,
          `Otros grupos: ${this.totalGroups - this.groupsWithAtLeastOneComplete}`,
        ],
        datasets: [
          {
            data: [
              this.groupsWithAtLeastOneComplete,
              this.totalGroups - this.groupsWithAtLeastOneComplete,
            ],
            backgroundColor: ["#AAD6F2", "#d5d5d5"],
            hoverBackgroundColor: ["#AAD6F2", "#d5d5d5"],
          },
        ],
      };
    },
    userCompletenessChartData() {
      return {
        labels: [
          `Usuarios Completos: ${this.totalCompleteUsers}`,
          `Usuarios Incompletos: ${this.totalIncompleteUsers}`,
        ],
        datasets: [
          {
            data: [this.totalCompleteUsers, this.totalIncompleteUsers],
            backgroundColor: ["#59B4F1", "#d5d5d5"],
            hoverBackgroundColor: ["#59B4F1", "#d5d5d5"],
          },
        ],
      };
    },
    usuariosConDatosQR() {
      let count = 0;
      Object.values(this.groups).forEach((group) => {
        (group.staffs?.usuarios || []).forEach((user) => {
          if (user.nombres && user.correo_electronico) count++;
        });
      });
      return count;
    },
    qrChartData() {
      const totalUsuarios = Object.values(this.groups).reduce(
        (acc, group) => acc + (group.staffs?.usuarios?.length || 0),
        0
      );
      const usuariosConQR = this.usuariosConDatosQR;
      return {
        labels: [
          `Con datos para QR: ${usuariosConQR}`,
          `Incompletos: ${totalUsuarios - usuariosConQR}`,
        ],
        datasets: [
          {
            data: [usuariosConQR, totalUsuarios - usuariosConQR],
            backgroundColor: ["#AAD6F2", "#d5d5d5"],
            hoverBackgroundColor: ["#AAD6F2", "#d5d5d5"],
          },
        ],
      };
    },
  },
  methods: {
    normalizeEventLabel(value, fallback) {
      const label = String(value || "").trim();
      return label || fallback;
    },
    applyEventLabels(labels = {}) {
      this.labelStaff = this.normalizeEventLabel(labels.staff, "Staff");
      this.labelAsistentes = this.normalizeEventLabel(labels.asistentes, "Asistentes");
      this.labelExpositor = this.normalizeEventLabel(labels.conferencistas, "Expositores");
    },

    // ----------------------------
    // API
    // ----------------------------
    fetchGroups() {
      return axios
        .post("/api/list/members-staff", {
          token_evento: this.eventoToken,
          Authorization: this.$store.state.user.token,
          include_wordpress: this.includeWordpress,
          event_scope: this.eventScope,
        })
        .then((response) => {
          this.groups = response.data.evento || {};
          this.applyEventLabels(response.data.labels || {});
          this.eventContext = response.data.event_context || {};
          this.eventSchemas = response.data.event_schemas || {};
          this.selectedEventIds = response.data.selected_event_ids || [];
          this.eventScope = response.data.event_scope || "current";

          const raw = JSON.parse(response.data.campos_obligatorios || "[]");

          // metadata completa para export
          this.camposMeta = raw
            .filter((f) => f && f.label)
            .map((f) => ({
              label: f.label,
              nombre_del_campo: f.nombre_del_campo ?? "",
              tipo_ui: f.tipo_ui ?? "input",
              opciones: Array.isArray(f.opciones) ? f.opciones : [],
              activo: !!f.activo,
              requerido: !!f.requerido,
              mandatorio: !!f.mandatorio,
              custom: !!f.custom,
            }));

          // map requerido/mandatorio para “complete”
          this.camposObligatorios = raw.reduce((acc, field) => {
            if (field?.activo && (field?.requerido || field?.mandatorio)) {
              acc[field.label] = true;
            }
            return acc;
          }, {});
        })
        .catch((error) => {
          console.error("Error fetching members:", error);
        });
    },

    // ----------------------------
    // Completeness helpers
    // ----------------------------
    countComplete(users, category, group = null) {
      return (users || []).filter((u) => u.escarapela === category && this.isUserComplete(u, group)).length;
    },
    countRegistered(users, category) {
      return (users || []).filter((u) => u.escarapela === category && this.isMeaningfulRegisteredUser(u)).length;
    },
    totalCompleteUsersGroup(users) {
      return (users || []).filter((u) => this.isUserComplete(u)).length;
    },
    totalRegisteredUsersGroup(users) {
      return (users || []).filter((u) => this.isMeaningfulRegisteredUser(u)).length;
    },
    totalAllowedUsersGroup(group) {
      return (
        Number(group?.datadelgrupo?.cantidad_staff || 0) +
        Number(group?.datadelgrupo?.cantidad_asistente || 0) +
        Number(group?.datadelgrupo?.cantidad_conferencista || 0)
      );
    },
    categorySummaryText(group, category, capacityField) {
      const registered = this.countRegistered(group?.staffs?.usuarios || [], category);
      const capacity = Number(group?.datadelgrupo?.[capacityField] || 0);
      return capacity > 0 ? `${registered} / ${capacity}` : String(registered);
    },
    computeTotalUsers(type) {
      return Object.values(this.groups).reduce((total, group) => {
        const users = group.staffs?.usuarios || [];
        const count = users.filter((u) => (type === "complete" ? this.isUserComplete(u, group) : !this.isUserComplete(u, group))).length;
        return total + count;
      }, 0);
    },
    computeGroupCounts(type) {
      return Object.values(this.groups).reduce((count, group) => {
        const users = group.staffs?.usuarios || [];
        const allComplete = users.length ? users.every((u) => this.isUserComplete(u, group)) : false;
        const anyIncomplete = users.some((u) => !this.isUserComplete(u, group));
        const anyComplete = users.some((u) => this.isUserComplete(u, group));

        if (type === "complete" && allComplete) return count + 1;
        if (type === "incomplete" && anyIncomplete) return count + 1;
        if (type === "atLeastOneComplete" && anyComplete) return count + 1;
        return count;
      }, 0);
    },
    isUserComplete(user, group = null) {
      const eventId = group?.evento?.id_evento;
      const schema = this.eventSchemas?.[String(eventId)] || [];
      const keys = schema.length
        ? schema.filter((field) => field?.activo && (field?.requerido || field?.mandatorio)).map((field) => field.label)
        : Object.keys(this.camposObligatorios || {});
      return keys.every((field) => {
        const v = user?.[field];
        if (v == null) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (Array.isArray(v)) return v.length > 0;
        return String(v).trim() !== "";
      });
    },
    isMeaningfulRegisteredUser(user) {
      if (!user || typeof user !== "object") return false;

      const activeLabels = (this.camposMeta || [])
        .filter((f) => f && f.activo && f.label)
        .map((f) => f.label);
      const labels = activeLabels.length
        ? activeLabels
        : ["nombres", "apellidos", "numero_de_documento", "correo_electronico", "telefono"];

      return labels.some((field) => {
        const value = user?.[field];
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.some((item) => String(item ?? "").trim() !== "");
        if (typeof value === "object") return Object.values(value).some((item) => String(item ?? "").trim() !== "");
        return String(value).trim() !== "";
      });
    },

    // ----------------------------
    // Excel helpers (opciones/select/multi)
    // ----------------------------
    _pickOptionText(o) {
      if (o == null) return "";
      if (typeof o === "string" || typeof o === "number") return String(o).trim();
      if (typeof o === "object") return String(o.value ?? o.label ?? o.nombre ?? "").trim();
      return "";
    },
    _normOptions(opciones) {
      const arr = Array.isArray(opciones) ? opciones : [];
      return arr.map(this._pickOptionText).filter(Boolean);
    },
    _normOpt(s) {
      return String(s ?? "").trim().toLowerCase();
    },
    _slugify(s) {
      return String(s || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 40);
    },
    _colLetter(n) {
      let s = "";
      while (n > 0) {
        const m = (n - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    },
    _coerceSelectByOptions(raw, opciones) {
      const val = String(raw ?? "").trim();
      if (!val) return "";
      const opts = this._normOptions(opciones);
      const map = new Map(opts.map((o) => [this._normOpt(o), o]));
      return map.get(this._normOpt(val)) || "";
    },
    _coerceMultiByOptions(raw, opciones) {
      const text = String(raw ?? "").trim();
      if (!text) return "";
      const opts = this._normOptions(opciones);
      const map = new Map(opts.map((o) => [this._normOpt(o), o]));
      const parts = text
        .replace(/\n/g, ",")
        .replace(/;/g, ",")
        .replace(/\|/g, ",")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      const out = [];
      const seen = new Set();
      for (const p of parts) {
        const canon = map.get(this._normOpt(p));
        if (!canon) continue;
        if (seen.has(canon)) continue;
        seen.add(canon);
        out.push(canon);
      }
      return out.join(",");
    },
    _parseMultiSet(valueCsv, opciones) {
      const canon = this._coerceMultiByOptions(valueCsv, opciones);
      const set = new Set();
      canon
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((x) => set.add(x));
      return set;
    },

    // ----------------------------
    // Export Excel (NUEVO)
    // ----------------------------
    async exportToExcel() {
      if (this.$canView('superadministrador')) {
        const incluirWordpress = window.confirm("¿Deseas incluir el grupo WordPress en este reporte? Si eliges Cancelar, se excluirá de métricas y descarga.");
        if (incluirWordpress !== this.includeWordpress) {
          this.includeWordpress = incluirWordpress;
          await this.fetchGroups();
        }
      }

      // 1) columnas dinámicas
      const { columns, colMeta } = this._buildExportColumns();

      // 2) data rows
      const rows = this._buildExportRows(colMeta);

      const wb = new ExcelJS.Workbook();
      wb.creator = "Invitaciones Sponsors";
      wb.created = new Date();

      const ws = wb.addWorksheet("Usuarios");
      const wsList = wb.addWorksheet("Listas");
      wsList.state = "hidden";

      ws.columns = columns;

      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = { vertical: "middle", horizontal: "center", wrapText: true };

      rows.forEach((r) => ws.addRow(r));

      // 3) listas + validaciones
      this._applyListsAndValidations(ws, wsList, colMeta, rows.length);

      // 4) download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const scopeSuffix = this.eventScope === "family"
        ? "familia-eventos"
        : this.eventScope === "parent"
          ? "evento-principal"
          : this.eventScope.startsWith("event:")
            ? "subevento"
            : "evento-actual";
      a.download = `usuarios-registrados-${scopeSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    _buildExportColumns() {
      const cols = [];
      const meta = [];

      // Base
      cols.push({ header: "Evento", key: "evento", width: 30 });
      meta.push({ kind: "base" });

      cols.push({ header: "Grupo", key: "grupo", width: 28 });
      meta.push({ kind: "base" });

      cols.push({ header: "Tipo de usuario", key: "escarapela", width: 20 });
      meta.push({ kind: "base" });

      cols.push({ header: "Autorizado", key: "autorizado", width: 12 });
      meta.push({ kind: "base" });

      cols.push({ header: "staff_id", key: "staff_id", width: 12 });
      meta.push({ kind: "base" });

      // Campos activos (incluye custom)
      const active = (this.camposMeta || []).filter((f) => f.activo && f.label);

      for (const f of active) {
        const label = f.label;
        const name = String(f.nombre_del_campo || humanize(label)).trim();
        const ui = String(f.tipo_ui || "input").toLowerCase();
        const opts = this._normOptions(f.opciones);

        if (ui === "multicheck" && opts.length > 0 && opts.length <= MAX_MULTI_OPTION_COLS) {
          for (const opt of opts) {
            const key = `${label}__${this._slugify(opt)}`;
            const header = `${name} — ${opt}`;
            cols.push({
              header,
              key,
              width: Math.max(18, Math.min(52, header.length + 4)),
            });
            meta.push({ kind: "multicheck_option", baseLabel: label, baseName: name, option: opt, opciones: opts });
          }
        } else {
          cols.push({
            header: name,
            key: label,
            width: Math.max(18, Math.min(42, name.length + 6)),
          });
          meta.push({
            kind: ui === "select" ? "select" : ui === "multicheck" ? "multicheck_csv" : "input",
            label,
            name,
            opciones: opts,
          });
        }
      }

      return { columns: cols, colMeta: meta };
    },

    _exportCategoriesForGroup(group) {
      const data = group?.datadelgrupo || {};
      const labels = group?.labels || {};
      return [
        {
          escarapela: "Staff",
          label: labels.staff || this.labelStaff || "Staff",
          cantidad: Number(data.cantidad_staff || 0),
          visible: String(data.mostrar_staff ?? (Number(data.cantidad_staff || 0) > 0 ? "1" : "0")) === "1",
        },
        {
          escarapela: "Asistente",
          label: labels.asistentes || this.labelAsistentes || "Asistentes",
          cantidad: Number(data.cantidad_asistente || 0),
          visible: String(data.mostrar_asistente ?? (Number(data.cantidad_asistente || 0) > 0 ? "1" : "0")) === "1",
        },
        {
          escarapela: "Conferencista",
          label: labels.conferencistas || this.labelExpositor || "Expositores",
          cantidad: Number(data.cantidad_conferencista || 0),
          visible: String(data.mostrar_conferencista ?? (Number(data.cantidad_conferencista || 0) > 0 ? "1" : "0")) === "1",
        },
      ];
    },

    _isMeaningfulExportUser(user, colMeta) {
      if (!user || typeof user !== "object") return false;

      return (colMeta || []).some((meta) => {
        if (!meta || meta.kind === "base") return false;
        const field = meta.kind === "multicheck_option" ? meta.baseLabel : meta.label;
        const value = user?.[field];
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.some((item) => String(item ?? "").trim() !== "");
        if (typeof value === "object") return Object.values(value).some((item) => String(item ?? "").trim() !== "");
        return String(value).trim() !== "";
      });
    },

    _createExportRow(eventName, groupName, user, escarapela, colMeta) {
      const row = {
        evento: eventName,
        grupo: groupName,
        escarapela: escarapela ?? user?.escarapela ?? "",
        autorizado: user?.autorizado ?? "",
        staff_id: user?.staff_id ?? user?.id ?? user?.id_token ?? "",
      };

      // Para multicheck option columns, vamos llenándolas cuando aparezcan en meta
      // Para campos normales, con key=label
      for (const m of colMeta) {
        if (m.kind === "select") {
          row[m.label] = this._coerceSelectByOptions(user?.[m.label], m.opciones);
        } else if (m.kind === "multicheck_csv") {
          row[m.label] = this._coerceMultiByOptions(user?.[m.label], m.opciones);
        } else if (m.kind === "multicheck_option") {
          // set SI/blank según si está seleccionada
          const set = this._parseMultiSet(user?.[m.baseLabel], m.opciones);
          const key = `${m.baseLabel}__${this._slugify(m.option)}`;
          row[key] = set.has(m.option) ? "SI" : "";
        } else if (m.kind === "input") {
          row[m.label] = user?.[m.label] ?? "";
        }
      }

      return row;
    },

    _buildExportRows(colMeta) {
      const out = [];

      Object.values(this.groups || {}).forEach((group) => {
        const eventName = group?.evento?.nombre || this.eventContext?.current?.nombre || "";
        const groupName = group?.datadelgrupo?.name || "";
        const users = group?.staffs?.usuarios || [];

        this._exportCategoriesForGroup(group).forEach((category) => {
          const usersByCategory = users.filter((user) => user?.escarapela === category.escarapela);
          const realUsers = usersByCategory.filter((user) => this._isMeaningfulExportUser(user, colMeta));

          if (realUsers.length) {
            realUsers.forEach((user) => {
              out.push(this._createExportRow(eventName, groupName, user, category.label, colMeta));
            });
            return;
          }

          if (category.cantidad > 0 || category.visible || usersByCategory.length > 0) {
            out.push(this._createExportRow(eventName, groupName, {}, category.label, colMeta));
          }
        });
      });

      return out;
    },

    _applyListsAndValidations(ws, wsList, colMeta, rowCount) {
      // fila final para validaciones (min 2)
      const lastRow = Math.max(2, rowCount + 1);

      // tipo documento fijo en A
      wsList.getCell("A1").value = "tipo_de_documento";
      wsList.getCell("A2").value = "CC";
      wsList.getCell("A3").value = "CE";
      wsList.getCell("A4").value = "Pasaporte";
      const tipoDocRange = "Listas!$A$2:$A$4";

      // SI/NO en B
      wsList.getCell("B1").value = "SI_NO";
      wsList.getCell("B2").value = "SI";
      wsList.getCell("B3").value = "NO";
      const siNoRange = "Listas!$B$2:$B$3";

      let listColCursor = 3; // C para selects dinámicos

      // recorremos columnas del WS (1-based)
      for (let c = 1; c <= ws.columnCount; c++) {
        const m = colMeta[c - 1];
        const headerCell = ws.getCell(1, c);

        // multicheck option columns: SI/NO
        if (m?.kind === "multicheck_option") {
          headerCell.note = "Marca SI para incluir esta opción. (Vacío/NO = no incluir)";
          for (let r = 2; r <= lastRow; r++) {
            ws.getCell(r, c).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [siNoRange],
              showErrorMessage: true,
              errorTitle: "Valor inválido",
              error: "Usa SI o NO.",
            };
          }
          continue;
        }

        // selects
        if (m?.kind === "select") {
          // tipo_de_documento: lista fija
          if (m.label === "tipo_de_documento") {
            headerCell.note = "Valores permitidos: CC, CE, Pasaporte";
            for (let r = 2; r <= lastRow; r++) {
              ws.getCell(r, c).dataValidation = {
                type: "list",
                allowBlank: true,
                formulae: [tipoDocRange],
                showErrorMessage: true,
                errorTitle: "Valor inválido",
                error: "Usa CC, CE o Pasaporte.",
              };
            }
            continue;
          }

          // otros selects: si hay opciones, escribimos lista en hoja oculta
          if (Array.isArray(m.opciones) && m.opciones.length) {
            const colNum = listColCursor++;
            const letter = this._colLetter(colNum);

            wsList.getCell(`${letter}1`).value = m.label;
            m.opciones.forEach((opt, i) => {
              wsList.getCell(`${letter}${2 + i}`).value = opt;
            });

            const range = `Listas!$${letter}$2:$${letter}$${1 + m.opciones.length}`;
            headerCell.note = `Selector. Valores permitidos: ${m.opciones.join(", ")}`;

            for (let r = 2; r <= lastRow; r++) {
              ws.getCell(r, c).dataValidation = {
                type: "list",
                allowBlank: true,
                formulae: [range],
                showErrorMessage: true,
                errorTitle: "Valor inválido",
                error: "Selecciona un valor permitido desde el dropdown.",
              };
            }
          }
        }

        // multicheck_csv: nota de ayuda
        if (m?.kind === "multicheck_csv") {
          if (m.opciones && m.opciones.length) {
            headerCell.note = `Multiselección: escribe opciones separadas por coma. Permitidas: ${m.opciones.join(", ")}`;
          } else {
            headerCell.note = "Multiselección: escribe opciones separadas por coma.";
          }
        }
      }
    },
  },

  watch: {
    permissionsReady: {
      immediate: true,
      handler(isReady) {
        if (!isReady) return;

        if (!this.groupsInitialized) {
          this.includeWordpress = this.canSeeWordpress;
          this.groupsInitialized = true;
          this.fetchGroups();
        }
      },
    },
  },
};
</script>

  <style scoped>
  .check-groups {
    max-width: 1200px;
    margin: auto;
    display: flex;
    flex-direction: column;
    min-height: 90vh;
    justify-content: flex-start;
    margin-top: 30px;
    padding-bottom: 120px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    border-radius: 15px;
    overflow: hidden;
  }
  
  th, td {
    border-top: 1px solid #ccc;
    background: #fff;
    padding: 8px;
    text-align: center;
  }
  th {
    background-color: #1f2937;
    color: #fff;
  }
  .button-excel {
    background-color: #4CAF50; /* Green */
    border: none;
    color: white;
    padding: 5px 12px;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    font-size: 14px;
    /* margin: 4px 2px 4px 2px; */
    cursor: pointer;
    border-radius: 10px;
  }
  .button-excel:hover{
    background-color: #378139; /* Green */

  }
  .contenedor-graficas{
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  

.export-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.wordpress-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
}

.scope-selector {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: min(100%, 520px);
  padding: 10px 12px;
  border-radius: 12px;
  background: #eff6ff;
  color: #1e3a8a;
  font-size: 0.9rem;
  font-weight: 600;
}

.scope-selector select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #93c5fd;
  border-radius: 8px;
  background: #fff;
  color: #1f2937;
}

</style>
  
